import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Globe, Package, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';

const heroSlides = [
  {
    id: 1,
    title: "Global Leaders in",
    highlight: "Consumer Electronics",
    subtitle: "Premium B2B solutions for TVs, Smartphones, Laptops & more",
    image: "https://images.unsplash.com/photo-1468495244123-6c6c332eeece?w=1920&q=80",
    cta: "Explore Products"
  },
  {
    id: 2,
    title: "Your Trusted Partner for",
    highlight: "IT Solutions",
    subtitle: "Enterprise-grade computing, networking & storage solutions",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&q=80",
    cta: "View Catalog"
  },
  {
    id: 3,
    title: "Connecting Worlds Through",
    highlight: "Technology",
    subtitle: "India • Hong Kong • UAE - Delivering excellence worldwide",
    image: "https://images.unsplash.com/photo-1713098965471-d324f294a71d?w=1920&q=80",
    cta: "Contact Us"
  }
];

const stats = [
  { icon: Globe, value: "3", label: "Global Offices" },
  { icon: Package, value: "500+", label: "Products" },
  { icon: Headphones, value: "24/7", label: "Support" },
];

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroSlides[currentSlide].image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/90 to-brand-dark/70" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
              >
                <motion.span 
                  className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 rounded-full glass text-brand-accent text-xs sm:text-sm font-medium mb-4 sm:mb-6"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  Est. 2018 • Trusted Worldwide
                </motion.span>
                
                <h1 className="font-outfit text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-3 sm:mb-4">
                  {heroSlides[currentSlide].title}{' '}
                  <span className="gradient-text">{heroSlides[currentSlide].highlight}</span>
                </h1>
                
                <p className="text-gray-300 text-base sm:text-lg md:text-xl mb-6 sm:mb-8 max-w-xl">
                  {heroSlides[currentSlide].subtitle}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Button 
                    onClick={() => scrollToSection('#products')}
                    className="bg-brand-accent hover:bg-brand-accent/90 text-brand-dark font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full w-full sm:w-auto"
                    data-testid="hero-cta-btn"
                  >
                    {heroSlides[currentSlide].cta}
                  </Button>
                  <Button 
                    onClick={() => scrollToSection('#contact')}
                    variant="outline"
                    className="border-brand-primary text-white hover:bg-brand-primary/20 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full w-full sm:w-auto"
                    data-testid="hero-contact-btn"
                  >
                    Contact Us
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Stats */}
            <motion.div 
              className="flex justify-center sm:justify-start gap-6 sm:gap-8 mt-8 sm:mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-brand-accent mx-auto mb-2" />
                  <div className="text-xl sm:text-2xl font-outfit font-bold text-white">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Slide Indicators */}
          <div className="hidden lg:flex flex-col items-end justify-center gap-4">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? 'bg-brand-accent w-12' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
                data-testid={`slide-indicator-${index}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Slide Indicators */}
      <div className="lg:hidden absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              currentSlide === index 
                ? 'bg-brand-accent w-6' 
                : 'bg-white/30'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full glass text-white hover:bg-white/10 transition-colors"
        aria-label="Previous slide"
        data-testid="hero-prev-btn"
      >
        <ChevronLeft size={20} className="sm:w-6 sm:h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full glass text-white hover:bg-white/10 transition-colors"
        aria-label="Next slide"
        data-testid="hero-next-btn"
      >
        <ChevronRight size={20} className="sm:w-6 sm:h-6" />
      </button>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 hidden sm:block"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-brand-accent rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
